/// Name shamelessly stolen :)

import { getConfig } from "@bidbattles/bloks/config";
import { Result } from "@bidbattles/bloks/lib/result";
import {
	type QueryKey,
	infiniteQueryOptions,
	queryOptions,
} from "@tanstack/react-query";
import { z } from "zod/v4";
import {
	BBBackendError,
	type PageInfo,
	createPaginatedResponseSchema,
	createResponseSchema,
	fdFetch,
	jsonFetch,
	parseResponse,
	toSearchParams,
} from "./rapace/utils";

const optionalNullishString = z
	.string()
	.trim()
	.nullish()
	.transform((str) => str || undefined)
	.pipe(z.string().optional());

const SUBSCRIPTION_TIERS = ["FREE", "TRIAL", "PREMIUM"] as const;
const SUBSCRIPTION_STATUSES = ["canceled", "expired", "active"] as const;

const lowercase = z.string().transform((str) => str.toLowerCase());
const uppercase = z.string().transform((str) => str.toUpperCase());

const userSchema = z.object({
	id: z.uuid(),
	first_name: z.string(),
	last_name: z.string(),
	email: z.email(),
	activated: z.boolean(),
	username: optionalNullishString,
	image_url: optionalNullishString,
	created_at: z.coerce.date(),
	role: lowercase.pipe(z.enum(["user", "admin"])),
	subscription: z.object({
		status: lowercase.pipe(z.enum(SUBSCRIPTION_STATUSES)),
		tier: uppercase.pipe(z.enum(SUBSCRIPTION_TIERS)),
		expiry: z.coerce.date().optional(),
	}),
});
const loyaltyPointsSchema = z.object({
	balance: z.number(),
});
export type User = z.infer<typeof userSchema> & {
	full_name: string;
	display_name: string;
	loyalty_points: z.infer<typeof loyaltyPointsSchema>;
	profile?: UserProfile;
};
export type UserSubscription = User["subscription"];

const userProfileSchema = z.object({
	user_id: z.uuid(),
	country: optionalNullishString,
	address: optionalNullishString,
	gender: optionalNullishString
		.pipe(uppercase.optional())
		.pipe(z.enum(["M", "F"]).optional()),
	phone_number: optionalNullishString,
	date_of_birth: z.preprocess((v) => {
		if (typeof v === "string") return new Date(v);
		return undefined;
	}, z.date().optional()),
});
export type UserProfile = z.infer<typeof userProfileSchema>;

const baseBattleSchema = z.object({
	id: z.uuid(),
	status: uppercase.pipe(
		z.enum(["OPEN", "FINISHED", "LIVE", "FULL", "PAUSED", "STOPPED"]),
	),
	reserved: z.boolean(),
	item_name: z.string(),
	item_description: z.string(),
	open_seats: z.number(),
	total_seats: z.number(),
	entry_price: z.number(),
	battle_duration: z.number(),
	winner_details: z
		.object({
			id: z.uuid(),
			name: z.string(),
		})
		.optional(),
});

const battleSchema = z.intersection(
	baseBattleSchema,
	z.object({
		item_image_url: z.url(),
	}),
);
export type BidBattle = z.infer<typeof battleSchema>;

const battleDetailsSchema = z.intersection(
	baseBattleSchema,
	z.object({
		item_image_urls: z.array(z.url()),
		reservations: z.array(z.string()),
	}),
);
export type BidBattleDetails = z.infer<typeof battleDetailsSchema>;

export const BidTypes = ["micro", "normal"] as const;
const bidTypeSchema = lowercase.pipe(z.enum(BidTypes));
export type BidType = z.infer<typeof bidTypeSchema>;

const bidPriceSchema = z.object({
	id: z.uuid(),
	bid_type: bidTypeSchema,
	seconds: z.number(),
	price: z.number(),
});
export type BidPrice = z.infer<typeof bidPriceSchema>;

const bidPackageSchema = z.object({
	id: z.uuid(),
	bid_type: bidTypeSchema,
	name: z.string(),
	total_price: z.number(),
	total_bids: z.number(),
	is_included: z.boolean(),
	bid_prices: z.array(
		bidPriceSchema.extend({
			quantity: z.number(),
			total_price: z.number(),
		}),
	),
});
export type BidPackage = z.infer<typeof bidPackageSchema>;

const adminBidTypeSchema = z.object({
	id: z.uuid(),
	name: bidTypeSchema,
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});
export type AdminBidType = z.infer<typeof adminBidTypeSchema>;

const adminBidPriceSchema = z.object({
	id: z.uuid(),
	bid_type_id: z.uuid(),
	seconds: z.number(),
	price: z.number(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});
export type AdminBidPrice = z.infer<typeof adminBidPriceSchema>;

const adminBidPackageSummarySchema = z.object({
	id: z.uuid(),
	bid_type_id: z.uuid(),
	name: z.string(),
	created_at: z.coerce.date(),
	updated_at: z.coerce.date(),
});
export type AdminBidPackageSummary = z.infer<
	typeof adminBidPackageSummarySchema
>;

const adminBidPackageSchema = adminBidPackageSummarySchema.extend({
	bid_prices: z.array(
		z.object({
			id: z.uuid(),
			seconds: z.number(),
			price: z.number(),
			quantity: z.number(),
		}),
	),
});
export type AdminBidPackage = z.infer<typeof adminBidPackageSchema>;

const checkoutSessionSchema = z.object({
	payment_id: z.uuid(),
	checkout_session_id: z.string(),
	checkout_url: z.string(),
});
export type CheckoutSession = z.infer<typeof checkoutSessionSchema>;
export type CheckoutBidItem = { bid_price_id: string; quantity: number };

const paymentItemSchema = z.object({
	id: z.uuid(),
	reference_id: z.uuid(),
	context_battle_id: z.uuid(),
	battle_name: z.string().optional(),
	item_type: lowercase.pipe(
		z.enum([
			"battle_entry",
			"bid_purchase",
			"bid_package_purchase",
			"subscription_purchase",
		]),
	),
	item_name: z.string().optional(),
	quantity: z.number().optional(),
});
export type PaymentItem = z.infer<typeof paymentItemSchema>;

const paymentStatuses = [
	"canceled",
	"failed",
	"paid",
	"pending",
	"authorized",
	"expired",
	"completed",
	"granted",
] as const;
const paymentStatusSchema = lowercase
	.pipe(z.enum(paymentStatuses))
	.or(z.string());
const paymentDetailsSchema = z.object({
	id: z.uuid(),
	provider_checkout_id: z.string(),
	provider: z.string(),
	status: paymentStatusSchema,
	amount: z.number(),
	payment_items: z.array(paymentItemSchema),
	created_at: z.coerce.date(),
});
export type PaymentDetails = z.infer<typeof paymentDetailsSchema>;

export const BidItemQualities = [
	"N/A",
	"NEW",
	"USED",
	"DAMAGED",
	"BROKEN",
] as const;
export type BidItemQuality = (typeof BidItemQualities)[number];

const itemTagSchema = z.object({
	id: z.uuid(),
	name: z.string(),
});
export type BidItemTag = z.infer<typeof itemTagSchema>;

const bidItemSchema = z.object({
	id: z.uuid(),
	name: z.string(),
	title: z.string(),
	category_id: z.uuid(),
	category: z.string(),
	description: z.string(),
	image_url: z.array(z.url()),
	price: z.number(),
	meta: z.string(),
	tags: z.array(itemTagSchema),
	serial: z.string(),
	quality: z.enum(BidItemQualities),
});
export type BidItem = z.infer<typeof bidItemSchema>;

const itemCategorySchema = z.object({
	id: z.uuid(),
	name: z.string(),
});
export type BidItemCategory = z.infer<typeof itemCategorySchema>;

const loyaltyPointsTransactionHistoryItemSchema = z.object({
	id: z.uuid(),
	amount: z.number(),
	type: lowercase
		.pipe(
			z.enum([
				"referral_reward",
				"bids_to_points_conversion",
				"points_to_bids_conversion",
				"manual_adjustment",
			]),
		)
		.or(z.string()),
	name: z.string(),
	created_at: z.coerce.date(),
});
export type LoyaltyPointsTransactionHistoryItem = z.infer<
	typeof loyaltyPointsTransactionHistoryItemSchema
>;

const notificationSchema = z.object({
	id: z.uuid(),
	message: z.string(),
	read_at: z.coerce.date().optional(),
	created_at: z.coerce.date(),
});
export type UserNotification = z.infer<typeof notificationSchema>;

const notificationPreferencesSchema = z.object({
	email: z.boolean(),
	push: z.boolean(),
});
export type NotificationPreferences = z.infer<
	typeof notificationPreferencesSchema
>;

const subscriptionSchema = z.object({
	id: z.uuid(),
	name: z.enum(SUBSCRIPTION_TIERS),
	price: z.number(),
	weight: z.number(),
});
export type SubscriptionTier = z.infer<typeof subscriptionSchema>;

const userRecordListItemSchema = userSchema.and(loyaltyPointsSchema);
export type UserRecordListItem = z.infer<typeof userRecordListItemSchema>;

const userRecordSchema = userRecordListItemSchema;
export type UserRecord = z.infer<typeof userRecordSchema>;

const battleParticipantListItemSchema = z.object({
	id: z.uuid(),
	user_id: z.uuid(),
	first_name: z.string(),
	last_name: z.string(),
	reserved_at: z.coerce.date(),
});
export type BattleParticipantListItem = z.infer<
	typeof battleParticipantListItemSchema
>;

const userTransactionHistoryListItemSchema = z.object({
	id: z.uuid(),
	checkout_id: z.string(),
	payment_type: z.string(),
	status: paymentStatusSchema,
	amount: z.number(),
	updated_at: z.coerce.date(),
});
export type UserTransactionHistoryListItem = z.infer<
	typeof userTransactionHistoryListItemSchema
>;

type PaginationParams = {
	query?: string;
	next?: string;
	limit?: number;
};

export const BBBackendClient = {
	async listBattles(
		params: PaginationParams & {
			reserved?: boolean;
			winner?: boolean;
			completed?: boolean;
		} = {},
	): Promise<Result<{ battles: BidBattle[]; pagination: PageInfo }>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: "/v1/battles",
					query: new URLSearchParams(toSearchParams(params)),
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createPaginatedResponseSchema(battleSchema),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				const { data: battles, pagination } = parseResult.data;

				return Result.ok({ battles, pagination });
			}),
		);
	},

	async fetchBattle(params: { id: string }): Promise<Result<BidBattleDetails>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: `/v1/battles/${params.id}`,
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(battleDetailsSchema),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(parseResult.data.data);
			}),
		);
	},

	async me(): Promise<Result<User | null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const [userResult, loyaltyResult, profileResult] = await Promise.all([
					jsonFetch({
						method: "GET",
						endpoint: "/v1/user",
					}),
					jsonFetch({
						method: "GET",
						endpoint: "/v1/loyalty/balance",
					}),
					jsonFetch({
						method: "GET",
						endpoint: "/v1/user/profile",
					}),
				]);

				if (!userResult.ok) {
					return userResult;
				}

				const userParseResult = await parseResponse(
					createResponseSchema(userSchema),
					userResult.data,
				);

				if (!userParseResult.ok) {
					if (
						userParseResult.error instanceof BBBackendError &&
						userParseResult.error.message === "unauthorized"
					) {
						return Result.ok(null);
					}

					return userParseResult;
				}

				if (!loyaltyResult.ok) {
					return loyaltyResult;
				}

				if (!profileResult.ok) {
					return profileResult;
				}

				const [loyaltyParseResult, profileParseResult] = await Promise.all([
					parseResponse(
						createResponseSchema(loyaltyPointsSchema),
						loyaltyResult.data,
					),
					parseResponse(
						createResponseSchema(userProfileSchema),
						profileResult.data,
					),
				]);

				if (!loyaltyParseResult.ok) {
					return loyaltyParseResult;
				}

				let profile: UserProfile | undefined = undefined;

				if (!profileParseResult.ok) {
					if (
						!(
							profileParseResult.error instanceof BBBackendError &&
							profileParseResult.error.message.includes("not found")
						)
					) {
						return profileParseResult;
					}
				} else {
					profile = profileParseResult.data.data;
				}

				const baseUser = userParseResult.data.data;
				const full_name = `${baseUser.first_name} ${baseUser.last_name}`;

				const user = {
					...baseUser,
					full_name,
					display_name: baseUser.username ?? full_name,
					loyalty_points: loyaltyParseResult.data.data,
					profile,
				};
				return Result.ok(user);
			}),
		);
	},

	async fetchReferralCode(): Promise<Result<string>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: "/v1/referral/code",
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.string()),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(parseResult.data.data);
			}),
		);
	},

	async logout(): Promise<Result<null>> {
		return Result.flatten(await Result.tryAsync(() => getConfig().logout()));
	},

	async listBidPricesForBattle(params: { battle_id: string }): Promise<
		Result<Array<BidPrice>>
	> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: `/v1/bids/price/${params.battle_id}`,
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.array(bidPriceSchema)),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(parseResult.data.data);
			}),
		);
	},

	async listBidPackagesForBattle(params: { battle_id: string }): Promise<
		Result<BidPackage[]>
	> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: `/v1/bids/package/${params.battle_id}`,
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.array(bidPackageSchema)),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(parseResult.data.data);
			}),
		);
	},

	async createCheckoutSession(
		params:
			| {
					battle_id: string;
					bid_items: Array<CheckoutBidItem>;
					item_type: "battle_entry" | "bid_purchase";
					card_token?: string;
					redirect_url: string;
			  }
			| {
					battle_id: string;
					bid_package_id: string;
					item_type: "battle_entry" | "bid_package_purchase";
					card_token?: string;
					redirect_url: string;
			  }
			| {
					subscription_tier_id: string;
					item_type: "subscription_purchase";
					card_token?: string;
					redirect_url: string;
			  },
	): Promise<Result<CheckoutSession>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "POST",
					endpoint: "/v1/payments/checkout",
					body: { data: params },
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(checkoutSessionSchema),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(parseResult.data.data);
			}),
		);
	},

	async listPayments(
		params: PaginationParams = {},
	): Promise<Result<{ payments: PaymentDetails[]; pagination: PageInfo }>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: "/v1/payments",
					query: new URLSearchParams(toSearchParams(params)),
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.array(paymentDetailsSchema)),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				const { data: payments } = parseResult.data;

				return Result.ok({
					payments,
					pagination: {
						has_next: false,
						has_prev: false,
						next_cursor: "",
					},
				});
			}),
		);
	},
	async fetchPaymentDetails(params: { id: string }): Promise<
		Result<PaymentDetails>
	> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: `/v1/payments/${params.id}`,
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(paymentDetailsSchema),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(parseResult.data.data);
			}),
		);
	},

	async listLoyaltyPointsTransactionHistory(
		params: Omit<PaginationParams, "query"> = {},
	): Promise<
		Result<{
			history: LoyaltyPointsTransactionHistoryItem[];
			pagination: PageInfo;
		}>
	> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: "/v1/loyalty/transactions",
					query: new URLSearchParams(toSearchParams(params)),
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createPaginatedResponseSchema(
						loyaltyPointsTransactionHistoryItemSchema,
					),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				const { data: history, pagination } = parseResult.data;

				return Result.ok({ history, pagination });
			}),
		);
	},

	async updatePassword(params: {
		current_password: string;
		new_password: string;
	}): Promise<Result<null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "POST",
					endpoint: "/v1/user/update_password",
					body: { data: params },
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},

	async updateUserProfile({
		avatar,
		date_of_birth,
		...params
	}: {
		username: string;
		country?: string;
		address?: string;
		gender?: "M" | "F";
		phone_number?: string;
		date_of_birth?: Date;
		avatar?: File;
	}): Promise<Result<null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await fdFetch({
					method: "POST",
					endpoint: "/v1/user/profile",
					body: {
						...params,
						file: avatar,
						date_of_birth: date_of_birth?.toISOString(),
					},
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},

	async sendResetPasswordToken(params: {
		email: string;
	}): Promise<Result<null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "POST",
					endpoint: "/v1/user/send_reset_password_token",
					body: { data: params },
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},

	async resetPassword(params: {
		token: string;
		new_password: string;
	}): Promise<Result<null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "POST",
					endpoint: "/v1/user/reset_password",
					body: { data: params },
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},

	async listNotifications(
		params: PaginationParams & { status?: "read" | "unread" } = {},
	): Promise<
		Result<{ notifications: UserNotification[]; pagination: PageInfo }>
	> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: "/v1/notifications",
					query: new URLSearchParams(toSearchParams(params)),
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.array(notificationSchema)),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				const { data: notifications } = parseResult.data;

				return Result.ok({
					notifications,
					pagination: {
						has_next: false,
						has_prev: false,
						next_cursor: "",
					},
				});
			}),
		);
	},

	async markNotificationAsRead(params: { id: string }): Promise<Result<null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "PATCH",
					endpoint: `/v1/notifications/${params.id}/read`,
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},

	async fetchNotificationPreferences(): Promise<
		Result<NotificationPreferences>
	> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: "/v1/notifications/preferences",
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(notificationPreferencesSchema),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(parseResult.data.data);
			}),
		);
	},

	async updateNotificationPreferences(
		params: NotificationPreferences,
	): Promise<Result<null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "POST",
					endpoint: "/v1/notifications/preferences",
					body: { data: params },
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},

	async ensureNotificationSubscription(params: {
		endpoint: string;
		auth: string;
		p256dh: string;
	}): Promise<Result<null>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "POST",
					endpoint: "/v1/notifications/subscribe",
					body: { data: params },
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.unknown()),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(null);
			}),
		);
	},

	async listSubscriptionTiers(): Promise<Result<Array<SubscriptionTier>>> {
		return Result.flatten(
			await Result.tryAsync(async () => {
				const responseResult = await jsonFetch({
					method: "GET",
					endpoint: "/v1/subscriptions",
				});

				if (!responseResult.ok) {
					return responseResult;
				}

				const parseResult = await parseResponse(
					createResponseSchema(z.array(subscriptionSchema)),
					responseResult.data,
				);

				if (!parseResult.ok) {
					return parseResult;
				}

				return Result.ok(parseResult.data.data);
			}),
		);
	},

	Admin: {
		async fetchBidItem(params: { id: string }): Promise<Result<BidItem>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: `/v1/admin/item/${params.id}`,
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(bidItemSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async listBidItems(
			params: PaginationParams = {},
		): Promise<Result<{ items: BidItem[]; pagination: PageInfo }>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: "/v1/admin/items",
						query: new URLSearchParams(toSearchParams(params)),
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createPaginatedResponseSchema(bidItemSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}
					const { data: items, pagination } = parseResult.data;

					return Result.ok({ items, pagination });
				}),
			);
		},

		async listBidItemCategories(
			params: PaginationParams = {},
		): Promise<
			Result<{ categories: BidItemCategory[]; pagination: PageInfo }>
		> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: "/v1/categories",
						query: new URLSearchParams(toSearchParams(params)),
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createPaginatedResponseSchema(itemCategorySchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					const { data: categories, pagination } = parseResult.data;

					return Result.ok({ categories, pagination });
				}),
			);
		},

		async listBidItemTags(
			params: PaginationParams = {},
		): Promise<Result<{ tags: BidItemTag[]; pagination: PageInfo }>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: "/v1/tags",
						query: new URLSearchParams(toSearchParams(params)),
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createPaginatedResponseSchema(itemTagSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					const { data: tags, pagination } = parseResult.data;

					return Result.ok({ tags, pagination });
				}),
			);
		},

		async createBidItemCategory(params: { name: string }): Promise<
			Result<BidItemCategory>
		> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: "/v1/admin/categories",
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(itemCategorySchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async createBidItemTag(params: { name: string }): Promise<
			Result<BidItemTag>
		> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: "/v1/admin/tags",
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(itemTagSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async listBidTypes(): Promise<Result<AdminBidType[]>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: "/v1/admin/bids/types",
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.array(adminBidTypeSchema)),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async createBidType(params: {
			name: BidType;
		}): Promise<Result<AdminBidType>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: "/v1/admin/bids/type",
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(adminBidTypeSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async listBidPrices(
			params: { bid_type_id?: string } = {},
		): Promise<Result<AdminBidPrice[]>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: "/v1/admin/bids/prices",
						query: new URLSearchParams(toSearchParams(params)),
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.array(adminBidPriceSchema)),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async createBidPrice(params: {
			bid_type_id: string;
			seconds: number;
			price: number;
		}): Promise<Result<AdminBidPrice>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: "/v1/admin/bids/price",
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(adminBidPriceSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async updateBidPrice({
			bid_price_id,
			...params
		}: {
			bid_price_id: string;
			seconds?: number;
			price?: number;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "PATCH",
						endpoint: `/v1/admin/bids/price/${bid_price_id}`,
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async listBidPackages(
			params: { bid_type_id?: string } = {},
		): Promise<Result<AdminBidPackage[]>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: "/v1/admin/bids/packages",
						query: new URLSearchParams(toSearchParams(params)),
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.array(adminBidPackageSchema)),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async createBidPackage(params: {
			bid_type_id: string;
			name: string;
			bid_prices: Array<{ id: string; quantity: number }>;
		}): Promise<Result<AdminBidPackageSummary>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: "/v1/admin/bids/package",
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(adminBidPackageSummarySchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async updateBidPackage({
			bid_package_id,
			...params
		}: {
			bid_package_id: string;
			bid_type_id?: string;
			name?: string;
			bid_prices?: Array<{ id: string; quantity: number }>;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "PATCH",
						endpoint: `/v1/admin/bids/package/${bid_package_id}`,
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async createBidItem(params: {
			category_id: string;
			title: string;
			name: string;
			quality: BidItemQuality;
			description: string;
			serial: string;
			meta: string;
			price: string;
			tags: string[];
			files: File[];
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await fdFetch({
						method: "POST",
						endpoint: "/v1/admin/items",
						body: params,
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async createBidBattle(params: {
			item_id: string;
			total_seats: number;
			battle_duration: number;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: "/v1/admin/battles",
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async toggleLiveBattleStatus(params: {
			id: string;
			status: "PAUSE" | "RESUME" | "STOP";
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: `/v1/admin/battles/${params.id}/status`,
						body: { data: { status: params.status } },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async listUsers(
			params: PaginationParams = {},
		): Promise<Result<{ users: UserRecordListItem[]; pagination: PageInfo }>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: "/v1/admin/users",
						query: new URLSearchParams(toSearchParams(params)),
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createPaginatedResponseSchema(userRecordListItemSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					const { data: users, pagination } = parseResult.data;

					return Result.ok({ users, pagination });
				}),
			);
		},

		async fetchUser(params: { id: string }): Promise<Result<UserRecord>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: `/v1/admin/users/${params.id}`,
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(userRecordSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async fetchUserProfile(params: { id: string }): Promise<
			Result<UserProfile>
		> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: `/v1/admin/users/${params.id}/profile`,
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(userProfileSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(parseResult.data.data);
				}),
			);
		},

		async updateUserRole(params: {
			id: string;
			role: User["role"];
			reason: string;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "PATCH",
						endpoint: `/v1/admin/users/${params.id}/role`,
						body: { data: { role: params.role, reason: params.reason } },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async listBattleParticipants({
			id,
			...params
		}: { id: string } & PaginationParams): Promise<
			Result<{
				participants: BattleParticipantListItem[];
				pagination: PageInfo;
			}>
		> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: `/v1/admin/battles/${id}/participants`,
						query: new URLSearchParams(toSearchParams(params)),
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createPaginatedResponseSchema(battleParticipantListItemSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					const { data: participants, pagination } = parseResult.data;

					return Result.ok({ participants, pagination });
				}),
			);
		},

		async removeBattleParticipant(params: {
			battle_id: string;
			user_id: string;
			reason: string;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "DELETE",
						endpoint: `/v1/admin/battles/${params.battle_id}/participants`,
						body: { data: { user_id: params.user_id, reason: params.reason } },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async reconcilePayment(params: {
			user_id: string;
			checkout_session_id: string;
			reason: string;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: "/v1/admin/payments/reconcile",
						body: {
							data: {
								user_id: params.user_id,
								checkout_session_id: params.checkout_session_id,
								reason: params.reason,
							},
						},
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async adjustUserLoyaltyPoints(params: {
			user_id: string;
			operation: "INCREMENT" | "DECREMENT";
			delta: number;
			reason: string;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: `/v1/admin/users/${params.user_id}/loyalty`,
						body: {
							data: {
								operation: params.operation,
								delta: String(params.delta),
								reason: params.reason,
							},
						},
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async listUserTransactionHistory({
			user_id,
			...params
		}: { user_id: string } & Omit<PaginationParams, "query">): Promise<
			Result<{
				transactions: UserTransactionHistoryListItem[];
				pagination: PageInfo;
			}>
		> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "GET",
						endpoint: `/v1/admin/users/${user_id}/transaction_history`,
						query: new URLSearchParams(toSearchParams(params)),
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createPaginatedResponseSchema(userTransactionHistoryListItemSchema),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					const { data: transactions, pagination } = parseResult.data;

					return Result.ok({ transactions, pagination });
				}),
			);
		},

		async updateUserSubscription({
			user_id,
			...params
		}: {
			user_id: string;
			tier_id: string;
			status: Exclude<(typeof SUBSCRIPTION_STATUSES)[number], "expired">;
			expiry?: Date;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "POST",
						endpoint: `/v1/admin/users/${user_id}/subscription`,
						body: {
							data: params,
						},
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async deleteBattle(params: {
			battle_id: string;
			reason: string;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "DELETE",
						endpoint: `/v1/admin/battles/${params.battle_id}`,
						body: { data: { reason: params.reason } },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async updateBattle({
			battle_id,
			...params
		}: {
			battle_id: string;
			total_seats?: number;
			battle_duration?: number;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "PATCH",
						endpoint: `/v1/admin/battles/${battle_id}`,
						body: { data: params },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async deleteItem(params: {
			item_id: string;
			reason: string;
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "DELETE",
						endpoint: `/v1/admin/items/${params.item_id}`,
						body: { data: { reason: params.reason } },
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},

		async updateItem({
			item_id,
			...params
		}: {
			item_id: string;
			category_id: string;
			title: string;
			name: string;
			quality: BidItemQuality;
			description: string;
			serial: string;
			meta: string;
			price: string;
			tags: string[];
		}): Promise<Result<null>> {
			return Result.flatten(
				await Result.tryAsync(async () => {
					const responseResult = await jsonFetch({
						method: "PATCH",
						endpoint: `/v1/admin/items/${item_id}`,
						body: {
							data: {
								...params,
								tag_ids: params.tags,
							},
						},
					});

					if (!responseResult.ok) {
						return responseResult;
					}

					const parseResult = await parseResponse(
						createResponseSchema(z.unknown()),
						responseResult.data,
					);

					if (!parseResult.ok) {
						return parseResult;
					}

					return Result.ok(null);
				}),
			);
		},
	}, // Admin
};

function createQueryKeys<Keys extends string>(keys: Keys[]) {
	return Object.fromEntries(keys.map((key) => [key, key] as const)) as {
		[Key in Keys]: Key;
	};
}

export const BBBackendQueryKeys = createQueryKeys([
	"me",
	"item",
	"items",
	"tags",
	"categories",
	"battle",
	"battle_participants",
	"bid_types",
	"bid_prices",
	"bid_packages",
	"payment",
	"loyalty_points_tx_history",
	"referral_code",
	"notifications",
	"notification_preferences",
	"subscriptions",
	"user",
	"user_profile",
	"user_transactions",
]);

function queryFn<
	Output,
	Params extends unknown[],
	Key extends QueryKey = QueryKey,
>({
	fn,
	keyFn,
	...options
}: {
	fn: (...params: Params) => Promise<Result<Output>>;
	keyFn: (...params: Params) => Key;
	staleTime?: number;
}) {
	return (...params: Params) =>
		queryOptions({
			...options,
			queryKey: keyFn(...params),
			queryFn: async ({ queryKey }): Promise<Output> => {
				const result = await fn(...params);
				if (!result.ok && getConfig().isServer) {
					console.warn("[bidbattles:query] fetch failed", {
						queryKey,
						error: result.error,
					});
				}

				return Result.unwrap(result);
			},
		});
}

function infiniteQueryFn<
	Output extends { pagination: PageInfo },
	Params extends PaginationParams = PaginationParams,
	Key extends QueryKey = QueryKey,
>({
	fn,
	keyFn,
	...options
}: {
	fn: (params: Params) => Promise<Result<Output>>;
	keyFn: (params: Omit<Params, "next">) => Key;
	staleTime?: number;
}) {
	return (params: Omit<Params, "next">) =>
		infiniteQueryOptions<Output>({
			...options,
			queryKey: keyFn(params),
			getNextPageParam: (lastPage) => {
				const cursor = (lastPage as Output).pagination.next_cursor;
				if (cursor.trim() === "") {
					return null;
				}
				return cursor;
			},
			initialPageParam: undefined,
			queryFn: async ({ queryKey, pageParam }): Promise<Output> => {
				const result = await fn({ ...params, next: pageParam } as Params);
				if (!result.ok && getConfig().isServer) {
					console.warn("[bidbattles:query] fetch failed", {
						queryKey,
						error: result.error,
					});
				}

				return Result.unwrap(result);
			},
		});
}

export function infiniteQueryKey<Key extends QueryKey = QueryKey>(key: Key) {
	return [...key, "infinite"] as const;
}

export const BBBackendInfiniteQueryOptions = {
	listBattles: infiniteQueryFn({
		fn: BBBackendClient.listBattles,
		keyFn: (params) =>
			infiniteQueryKey([BBBackendQueryKeys.battle, params] as const),
	}),
	Admin: {
		listBidItemCategories: infiniteQueryFn({
			fn: BBBackendClient.Admin.listBidItemCategories,
			keyFn: (params) =>
				infiniteQueryKey([BBBackendQueryKeys.categories, params] as const),
		}),
		listBidItemTags: infiniteQueryFn({
			fn: BBBackendClient.Admin.listBidItemTags,
			keyFn: (params) =>
				infiniteQueryKey([BBBackendQueryKeys.tags, params] as const),
		}),
		listBidItems: infiniteQueryFn({
			fn: BBBackendClient.Admin.listBidItems,
			keyFn: (params) =>
				infiniteQueryKey([BBBackendQueryKeys.item, params] as const),
		}),
		listUsers: infiniteQueryFn({
			fn: BBBackendClient.Admin.listUsers,
			keyFn: (params) =>
				infiniteQueryKey([BBBackendQueryKeys.user, params] as const),
		}),
		listUserTransactionHistory: infiniteQueryFn({
			fn: BBBackendClient.Admin.listUserTransactionHistory,
			keyFn: (params) =>
				infiniteQueryKey([
					BBBackendQueryKeys.user_transactions,
					params,
				] as const),
		}),
	},
};

export const BBBackendQueryOptions = {
	listBattles: queryFn({
		fn: BBBackendClient.listBattles,
		keyFn: (params) => [BBBackendQueryKeys.battle, params, "list"],
	}),
	fetchBattle: queryFn({
		fn: BBBackendClient.fetchBattle,
		keyFn: (params) => [BBBackendQueryKeys.battle, params],
	}),
	me: queryFn({
		fn: BBBackendClient.me,
		keyFn: () => [BBBackendQueryKeys.me],
	}),
	fetchReferralCode: queryFn({
		fn: BBBackendClient.fetchReferralCode,
		keyFn: () => [BBBackendQueryKeys.referral_code],
	}),
	listBidPricesForBattle: queryFn({
		fn: BBBackendClient.listBidPricesForBattle,
		keyFn: (params) => [BBBackendQueryKeys.bid_prices, params],
	}),
	listBidPackagesForBattle: queryFn({
		fn: BBBackendClient.listBidPackagesForBattle,
		keyFn: (params) => [BBBackendQueryKeys.bid_packages, params],
	}),
	listPayments: queryFn({
		fn: BBBackendClient.listPayments,
		keyFn: (params) => [BBBackendQueryKeys.payment, params, "list"],
	}),
	fetchPaymentDetails: queryFn({
		fn: BBBackendClient.fetchPaymentDetails,
		keyFn: (params) => [BBBackendQueryKeys.payment, params],
	}),
	listLoyaltyPointsTransactionHistory: queryFn({
		fn: BBBackendClient.listLoyaltyPointsTransactionHistory,
		keyFn: (params) => [BBBackendQueryKeys.loyalty_points_tx_history, params],
	}),
	listNotifications: queryFn({
		fn: BBBackendClient.listNotifications,
		keyFn: (params) => [BBBackendQueryKeys.notifications, params],
		staleTime: 2.5 * 1000,
	}),
	fetchNotificationPreferences: queryFn({
		fn: BBBackendClient.fetchNotificationPreferences,
		keyFn: () => [BBBackendQueryKeys.notification_preferences],
	}),
	listSubscriptionTiers: queryFn({
		fn: BBBackendClient.listSubscriptionTiers,
		keyFn: () => [BBBackendQueryKeys.subscriptions],
		staleTime: 360 * 1000,
	}),

	Admin: {
		listBidTypes: queryFn({
			fn: BBBackendClient.Admin.listBidTypes,
			keyFn: () => [BBBackendQueryKeys.bid_types],
		}),
		listBidPrices: queryFn({
			fn: BBBackendClient.Admin.listBidPrices,
			keyFn: (params) => [BBBackendQueryKeys.bid_prices, params, "admin"],
		}),
		listBidPackages: queryFn({
			fn: BBBackendClient.Admin.listBidPackages,
			keyFn: (params) => [BBBackendQueryKeys.bid_packages, params, "admin"],
		}),
		fetchBidItem: queryFn({
			fn: BBBackendClient.Admin.fetchBidItem,
			keyFn: (params) => [BBBackendQueryKeys.item, params],
		}),
		listBidItems: queryFn({
			fn: BBBackendClient.Admin.listBidItems,
			keyFn: (params) => [BBBackendQueryKeys.item, params, "list"],
		}),
		listBidItemCategories: queryFn({
			fn: BBBackendClient.Admin.listBidItemCategories,
			keyFn: (params) => [BBBackendQueryKeys.categories, params],
		}),
		listBidItemTags: queryFn({
			fn: BBBackendClient.Admin.listBidItemTags,
			keyFn: (params) => [BBBackendQueryKeys.tags, params],
		}),
		listUsers: queryFn({
			fn: BBBackendClient.Admin.listUsers,
			keyFn: (params) => [BBBackendQueryKeys.user, params, "list"],
		}),
		fetchUser: queryFn({
			fn: BBBackendClient.Admin.fetchUser,
			keyFn: (params) => [BBBackendQueryKeys.user, params],
		}),
		fetchUserProfile: queryFn({
			fn: BBBackendClient.Admin.fetchUserProfile,
			keyFn: (params) => [BBBackendQueryKeys.user_profile, params],
		}),
		listBattleParticipants: queryFn({
			fn: BBBackendClient.Admin.listBattleParticipants,
			keyFn: (params) => [BBBackendQueryKeys.battle_participants, params],
		}),
		listUserTransactionHistory: queryFn({
			fn: BBBackendClient.Admin.listUserTransactionHistory,
			keyFn: (params) => [BBBackendQueryKeys.user_transactions, params],
		}),
	},
};
